import supabase from "@/supabase";
import * as Sentry from "@sentry/react";

export interface UploadResult {
  success: boolean;
  url?: string;
  message?: string;
}

/**
 * Upload a file to Supabase storage
 * @param file File to upload
 * @param bucket Bucket name
 * @param path Path within bucket
 * @returns Promise with upload result
 */
export const uploadFile = async (
  file: File,
  bucket: string,
  path: string
): Promise<UploadResult> => {
  try {
    // Upload file
    const { error } = await supabase.storage.from(bucket).upload(path, file, {
      cacheControl: "3600",
      upsert: true,
    });

    if (error) {
      Sentry.captureException(error, {
        extra: {
          context: "storageService.uploadFile",
          bucket,
          path,
        },
      });
      console.error("Error uploading file:", error);
      return {
        success: false,
        message: `Error uploading file: ${error.message}`,
      };
    }

    // Get public URL for the file
    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);

    return {
      success: true,
      url: urlData.publicUrl,
    };
  } catch (error) {
    console.error("Unexpected error in uploadFile:", error);
    return {
      success: false,
      message:
        error instanceof Error ? error.message : "An unexpected error occurred",
    };
  }
};

/**
 * Upload a document for an application
 * @param file File to upload
 * @param applicationId Application ID
 * @param documentType Document type (dvla-front, dvla-back, rtw, insurance)
 * @returns Promise with upload result
 */
export const uploadApplicationDocument = async (
  file: File,
  applicationId: string,
  documentType: string
): Promise<UploadResult> => {
  if (!applicationId) {
    return {
      success: false,
      message: "Application ID is required",
    };
  }

  if (!file) {
    return {
      success: false,
      message: "File is required",
    };
  }

  const fileExtension = file.name.split(".").pop();
  const timestamp = new Date().getTime();
  const path = `${applicationId}/${documentType}-${timestamp}.${fileExtension}`;

  return uploadFile(file, "application-documents", path);
};

/**
 * Upload a CV document
 * @param file File to upload
 * @param firstName Applicant's first name
 * @param lastName Applicant's last name
 * @returns Promise with upload result
 */
export const uploadCVDocument = async (
  file: File,
  firstName: string,
  lastName: string
): Promise<UploadResult> => {
  if (!file) {
    return {
      success: false,
      message: "File is required",
    };
  }

  if (!firstName || !lastName) {
    return {
      success: false,
      message: "First name and last name are required",
    };
  }

  const fileExtension = file.name.split(".").pop();
  const timestamp = new Date().getTime();
  // Use first name, last name, and timestamp for the file name
  const sanitizedFirstName = firstName.replace(/[^a-zA-Z0-9]/g, "");
  const sanitizedLastName = lastName.replace(/[^a-zA-Z0-9]/g, "");
  const path = `${sanitizedFirstName}-${sanitizedLastName}-${timestamp}.${fileExtension}`;

  // Use the dedicated 'cvs' bucket
  return uploadFile(file, "cvs", path);
};

/**
 * Delete a file from Supabase storage
 * @param bucket Bucket name
 * @param path Path within bucket
 * @returns Promise with delete result
 */
export const deleteFile = async (
  bucket: string,
  path: string
): Promise<{ success: boolean; message?: string }> => {
  try {
    const { error } = await supabase.storage.from(bucket).remove([path]);

    if (error) {
      console.error("Error deleting file:", error);
      return {
        success: false,
        message: `Error deleting file: ${error.message}`,
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    console.error("Unexpected error in deleteFile:", error);
    return {
      success: false,
      message:
        error instanceof Error ? error.message : "An unexpected error occurred",
    };
  }
};
